import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaPackages, saveSpaPackage, checkSpaPackageBillingUsage, exportSpaPackagesToExcel, exportSpaPackageDetailsToExcel } from "../../components/Api/GmsSpaPackage";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getTaxes } from "../../components/Api/GmsTax";
import { Eye, SquarePen, Plus, Minus, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PackageId: 0,
  PackageCode: "",
  PackageName_En: "",
  PackageName_Ar: "",
  Cost: 0,
  Amount: 0,
  DiscountAmount: 0,
  TaxAmount: 0,
  NetAmount: 0,
  Status: true,
  CreatedBy: 1,
  ModifiedBy: 1,
  Details: [],
  TaxIds: [],
  DiscountPercentage: 0,
};

const emptyFilter = {
  PackageId: null,
  PackageCode: "",
  PackageName_En: "",
  Status: null,
};

const defaultServiceDetail = {
  Service_Id: null,
  Quantity: 1,
};

export default function SpaPackagesPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaPackages');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaPackages');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaPackages');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaPackages');
  const isAdmin = isUserAdmin();

  const [spaPackages, setSpaPackages] = useState([]);
  const [spaServices, setSpaServices] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    return { ...defaultForm, Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [serviceDetails, setServiceDetails] = useState([defaultServiceDetail]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isUsedInBilling, setIsUsedInBilling] = useState(false);
  const [originalServiceDetails, setOriginalServiceDetails] = useState([]);
  const [billingUsageMap, setBillingUsageMap] = useState({});
  const [formKey, setFormKey] = useState(0);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadSpaServices(); // Load spa services first
      loadTaxes(); // Load taxes
      loadSpaPackages(); // Load spa packages on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadSpaPackages();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);



  // Calculate discount amount when amount or discount percentage changes
  useEffect(() => {
    calculateDiscountAmount();
  }, [form.Amount, form.DiscountPercentage]);

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({ Status: true }); // Only active services
      setSpaServices(data || []);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire("Error", "Failed to load spa services", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true }); // Only active taxes
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadSpaPackages = async () => {
    try {
      console.log("=== LOADING SPA PACKAGES ===");
      console.log("Filter:", filter);
      setLoading(true);
      const data = await getSpaPackages(filter);
      console.log("Loaded spa packages data:", data);
      
      // Ensure all numeric fields have default values to prevent toFixed errors
      const processedData = (data || []).map(item => {
        // Convert all numeric fields to numbers and provide defaults
        const cost = parseFloat(item.cost) || 0;
        const amount = parseFloat(item.amount) || 0;
        const discountAmount = parseFloat(item.discountAmount) || 0;
        const taxAmount = parseFloat(item.taxAmount) || 0;
        const netAmount = parseFloat(item.netAmount) || 0;
        
        return {
          ...item,
          PackageId: item.packageId,
          PackageCode: item.packageCode,
          PackageName_En: item.packageName_En,
          PackageName_Ar: item.packageName_Ar,
          Cost: cost,
          Amount: amount,
          DiscountAmount: discountAmount,
          DiscountPercentage: parseFloat(item.discountPercentage) || 0,
          TaxAmount: taxAmount,
          NetAmount: netAmount,
          Status: item.status,
          ServiceCount: item.serviceCount,
          CreatedOn: item.createdOn,
          CreatedBy: item.createdBy,
          ModifiedOn: item.modifiedOn,
          ModifiedBy: item.modifiedBy,
          Details: item.details,
          TaxIds: item.taxIds,
        };
      });
      
      setSpaPackages(processedData);
      setTotalCount(processedData.length);
      console.log("Total count set to:", processedData.length);
      
      // Check billing usage for all packages
      processedData.forEach(async (pkg) => {
        const packageId = pkg.PackageId || pkg.packageId;
        if (!billingUsageMap[packageId]) {
          await checkBillingUsageForPackage(packageId);
        }
      });
    } catch (error) {
      console.error("Error loading spa packages:", error);
      Swal.fire("Error", "Failed to load spa packages", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountAmount = () => {
    const baseAmount = parseFloat(form.Amount) || 0;
    const discountPercentage = parseFloat(form.DiscountPercentage) || 0;
    
    // Calculate discount amount from percentage
    const discountAmount = (baseAmount * discountPercentage) / 100;

    setForm(prev => ({
      ...prev,
      DiscountAmount: parseFloat(discountAmount.toFixed(2)),
      TaxAmount: 0,
      NetAmount: 0,
    }));
  };

  const handleSave = async () => {
    try {
      console.log("=== SAVE PROCESS STARTED ===");
      console.log("Form data:", form);
      console.log("Service details:", serviceDetails);
      console.log("Selected taxes:", selectedTaxes);
      
      // Validation
      if (!form.PackageName_En.trim()) {
        Swal.fire("Error", "Package Name (English) is required", "error");
        return;
      }

      // Validate service details haven't changed if package is used in billing
      if (isUsedInBilling && isEditing) {
        const currentServiceDetails = serviceDetails.map(detail => ({
          Service_Id: detail.Service_Id,
          Quantity: detail.Quantity
        }));
        
        const originalDetails = originalServiceDetails.map(detail => ({
          Service_Id: detail.Service_Id,
          Quantity: detail.Quantity
        }));
        
        const serviceDetailsChanged = JSON.stringify(currentServiceDetails.sort()) !== JSON.stringify(originalDetails.sort());
        
        if (serviceDetailsChanged) {
          Swal.fire({
            title: "Validation Error",
            text: "Services cannot be modified for packages that have been used in billing. Please restore the original service selection.",
            icon: "error",
            confirmButtonText: "OK"
          });
          return;
        }
      }

      if (!form.PackageCode.trim()) {
        Swal.fire("Error", "Package Code is required", "error");
        return;
      }

      if (serviceDetails.length === 0 || !serviceDetails.some(detail => detail.Service_Id)) {
        Swal.fire("Error", "At least one service must be selected", "error");
        return;
      }

      // Check for empty quantity fields
      const emptyQuantityDetails = serviceDetails.filter(detail => detail.Service_Id && (!detail.Quantity || detail.Quantity === ''));
      if (emptyQuantityDetails.length > 0) {
        Swal.fire("Error", "Please enter quantity for all selected services", "error");
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();
      console.log("User session:", session);
      
      // Prepare service details - map to correct API format
      const validServiceDetails = serviceDetails
        .filter(detail => detail.Service_Id && detail.Quantity > 0)
        .map(detail => ({
          Service_Id: detail.Service_Id,
          Quantity: detail.Quantity,
          Package_Id: form.PackageId || 0
        }));
      console.log("Valid service details:", validServiceDetails);
      
      // Calculate Net Amount in frontend
      const calculatedNetAmount = calculateNetAmount(form.Amount, form.DiscountPercentage, selectedTaxes);

      // Prepare save data - include dates like Product implementation
      const saveData = {
        ...form,
        NetAmount: calculatedNetAmount,
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: session.userId,
        CreatedOn: isEditing ? form.CreatedOn : new Date().toISOString(),
        ModifiedOn: new Date().toISOString(),
        Details: validServiceDetails,
        TaxIds: selectedTaxes.map(tax => tax.value),
      };

      console.log("Final save data being sent to API:", saveData);

      const result = await saveSpaPackage(saveData);
      console.log("API response:", result);
      
      // Check if save was successful based on API response
      if (result && (result.PackageId || result.packageId || result.id || result === "" || result.length === 0)) {
        Swal.fire("Success", "Spa Package saved successfully!", "success");
      } else {
        console.warn("Unexpected API response format:", result);
        Swal.fire("Success", "Spa Package saved successfully!", "success");
      }
      
      // Reset form and reload data
      handleClear();
      loadSpaPackages();
      // Stay on Add form tab (tab "1") instead of switching to list tab
    } catch (error) {
      console.error("Error saving spa package:", error);
      console.error("Error details:", error.response);
      let errorMessage = "Failed to save spa package";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response?.data === 'string') {
        // Extract error message from exception string
        const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
        errorMessage = match ? match[1].trim() : error.response.data;
      }
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const checkBillingUsageForPackage = async (packageId) => {
    try {
      console.log(`Checking billing usage for package ${packageId}...`);
      const billingUsageCheck = await checkSpaPackageBillingUsage(packageId);
      console.log(`Billing usage result for package ${packageId}:`, billingUsageCheck);
      
      setBillingUsageMap(prev => {
        const newMap = {
          ...prev,
          [packageId]: billingUsageCheck.isUsedInBilling
        };
        console.log("Updated billing usage map:", newMap);
        return newMap;
      });
      
      return billingUsageCheck.isUsedInBilling;
    } catch (error) {
      console.error(`Error checking billing usage for package ${packageId}:`, error);
      return false;
    }
  };

  const handleEdit = async (item) => {
    try {
      setEditLoadingRow(item.PackageId || item.packageId);
      
      // Check if package is used in billing
      const isUsed = await checkBillingUsageForPackage(item.PackageId || item.packageId);
      setIsUsedInBilling(isUsed);
      
      if (isUsed) {
        Swal.fire({
          title: "Warning",
          text: "This package has been used in billing. Only amount, discount, and tax can be modified. Package Code, Package Name, and Services cannot be changed.",
          icon: "warning",
          confirmButtonText: "Continue"
        });
      }
      
      // Keep all fields including dates like Product implementation
      const otherFields = item;

    setForm({
      PackageId: otherFields.PackageId || otherFields.packageId,
      PackageCode: otherFields.PackageCode || otherFields.packageCode,
      PackageName_En: otherFields.PackageName_En || otherFields.packageName_En,
      PackageName_Ar: (otherFields.PackageName_Ar || otherFields.packageName_Ar) || "",
      Cost: otherFields.Cost || otherFields.cost || 0,
      Amount: otherFields.Amount || otherFields.amount || 0,
      DiscountAmount: otherFields.DiscountAmount || otherFields.discountAmount || 0,
      DiscountPercentage: otherFields.DiscountPercentage || otherFields.discountPercentage || 0,
      TaxAmount: otherFields.TaxAmount || otherFields.taxAmount || 0,
      NetAmount: otherFields.NetAmount || otherFields.netAmount || 0,
      Status: otherFields.Status !== undefined ? otherFields.Status : otherFields.status,
      CreatedBy: otherFields.CreatedBy || otherFields.createdBy,
      CreatedOn: otherFields.CreatedOn || otherFields.createdOn,
      ModifiedOn: otherFields.ModifiedOn || otherFields.modifiedOn,
      ModifiedBy: otherFields.ModifiedBy || otherFields.modifiedBy,
    });

    // Set service details
    const packageServiceDetails = otherFields.Details || otherFields.details || [];
    if (packageServiceDetails.length > 0) {
      setServiceDetails(packageServiceDetails.map(detail => ({
        Service_Id: detail.Service_Id || detail.service_Id,
        Quantity: detail.Quantity || detail.quantity,
      })));
    } else {
      setServiceDetails([defaultServiceDetail]);
    }

    // Set selected taxes
    const packageTaxIds = otherFields.TaxIds || otherFields.taxIds || [];
    if (packageTaxIds.length > 0) {
      const selectedTaxOptions = taxOptions.filter(tax => 
        packageTaxIds.includes(tax.value)
      );
      setSelectedTaxes(selectedTaxOptions);
    } else {
      setSelectedTaxes([]);
    }

    setIsEditing(true);
    setActiveTab("1");
    
    // Store original service details for comparison
    const serviceDetails = otherFields.Details || otherFields.details || [];
    setOriginalServiceDetails(serviceDetails.map(detail => ({
      Service_Id: detail.Service_Id || detail.service_Id,
      Quantity: detail.Quantity || detail.quantity,
    })));
    
    } catch (error) {
      console.error("Error in handleEdit:", error);
      Swal.fire("Error", "Failed to load package for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (item) => {
    try {
      const details = item.Details || item.details;
      const taxIds = item.TaxIds || item.taxIds;
      
      // Get service names for display
      const serviceNames = details?.map(detail => {
        const serviceId = detail.Service_Id || detail.service_Id;
        const quantity = detail.Quantity || detail.quantity;
        const service = spaServices.find(s => s.spaService_Id === serviceId);
        return service ? `${service.spaServiceName_En} (Qty: ${quantity})` : `Service ID: ${serviceId} (Qty: ${quantity})`;
      }).join(', ') || 'No services';

      // Get tax names for display
      const taxNames = taxIds?.map(taxId => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        return tax ? `${tax.tax_NameEn} (${tax.tax_Perc}%)` : `Tax ID: ${taxId}`;
      }).join(', ') || 'No taxes';

      Swal.fire({
        title: `<div style="text-align: center; color: #556ee6; font-size: 24px; font-weight: bold; margin-bottom: 10px;">
                  <i class="bx bx-spa" style="margin-right: 10px;"></i>
                  Spa Package Details
                </div>`,
        html: `
          <div style="max-width: 700px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #556ee6;">
                <h6 style="color: #556ee6; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                  Basic Information
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Code:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${item.PackageCode || item.packageCode}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Name (English):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${item.PackageName_En || item.packageName_En}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Name (Arabic):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(item.PackageName_Ar || item.packageName_Ar) || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Status:</strong>
                  <span style="margin-left: 5px; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; ${(item.Status !== undefined ? item.Status : item.status) ? 'background: #d4edda; color: #155724;' : 'background: #f8d7da; color: #721c24;'}">
                    ${(item.Status !== undefined ? item.Status : item.status) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
                <h6 style="color: #28a745; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-money" style="margin-right: 5px;"></i>
                  Financial Details
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Cost:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.Cost || item.cost) || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Amount:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.Amount || item.amount) || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Discount Percentage:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.DiscountPercentage || item.discountPercentage) || 0).toFixed(2)}%</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057; font-size: 16px;">Net Amount:</strong>
                  <span style="color: #28a745; margin-left: 5px; font-size: 16px; font-weight: bold;">${((item.NetAmount || item.netAmount) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107; margin-bottom: 20px;">
              <h6 style="color: #ffc107; margin-bottom: 10px; font-weight: bold;">
                <i class="bx bx-list-check" style="margin-right: 5px;"></i>
                Services Included
              </h6>
              <div style="color: #6c757d; line-height: 1.6;">
                ${serviceNames}
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #dc3545;">
              <h6 style="color: #dc3545; margin-bottom: 10px; font-weight: bold;">
                <i class="bx bx-receipt" style="margin-right: 5px;"></i>
                Applied Taxes
              </h6>
              <div style="color: #6c757d; line-height: 1.6;">
                ${taxNames}
              </div>
            </div>
          </div>
        `,
        confirmButtonText: "Close",
        width: '800px',
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          confirmButton: 'btn btn-primary',
          popup: 'swal2-custom-popup'
        }
      });
    } catch (error) {
      console.error("Error viewing spa package:", error);
      Swal.fire("Error", "Failed to view spa package details", "error");
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    setForm({ ...defaultForm, Status: defaultStatus });
    setServiceDetails([{ Service_Id: null, Quantity: 1 }]);
    setSelectedTaxes([]);
    setIsEditing(false);
    setIsUsedInBilling(false);
    setOriginalServiceDetails([]);
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.PackageId !== null && filter.PackageId !== undefined && { PackageId: filter.PackageId }),
        ...(filter.PackageCode && filter.PackageCode.trim() !== '' && { PackageCode: filter.PackageCode }),
        ...(filter.PackageName_En && filter.PackageName_En.trim() !== '' && { PackageName_En: filter.PackageName_En }),
        ...(filter.Status !== null && filter.Status !== undefined && { Status: filter.Status })
      };
      
      await exportSpaPackagesToExcel(cleanFilter);
      Swal.fire("Success", "Spa package report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa packages:", error);
      Swal.fire("Error", "Failed to export spa package report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (packageId) => {
    try {
      setLoading(true);
      await exportSpaPackageDetailsToExcel(packageId);
      Swal.fire("Success", "Spa package details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa package details:", error);
      Swal.fire("Error", "Failed to export spa package details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Service detail management
  const addServiceDetail = () => {
    setServiceDetails([...serviceDetails, { ...defaultServiceDetail }]);
  };

  const removeServiceDetail = (index) => {
    if (serviceDetails.length > 1) {
      const newDetails = serviceDetails.filter((_, i) => i !== index);
      setServiceDetails(newDetails);
    }
  };

  const updateServiceDetail = (index, field, value) => {
    const newDetails = [...serviceDetails];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setServiceDetails(newDetails);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = spaPackages.slice(startIndex, endIndex);

  // Options for dropdowns
  const serviceOptions = useMemo(() => {
    return spaServices.map(service => ({
      value: service.spaService_Id,
      label: service.spaServiceName_En
    }));
  }, [spaServices]);

  // Get available service options for a specific dropdown (excluding already selected services)
  const getAvailableServiceOptions = (currentIndex) => {
    const selectedServiceIds = serviceDetails
      .map((detail, index) => index !== currentIndex ? detail.Service_Id : null)
      .filter(id => id !== null);
    
    return serviceOptions.filter(option => !selectedServiceIds.includes(option.value));
  };

  const taxOptions = useMemo(() => {
    return taxes.map(tax => ({
      value: tax.tax_Id,
      label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
    }));
  }, [taxes]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get service name by ID
  const getServiceName = (serviceId) => {
    const service = spaServices.find(s => s.spaService_Id === serviceId);
    return service ? service.spaServiceName_En : "N/A";
  };

  // Calculate Net Amount: Amount - (Amount * Discount%) + (Amount after discount * Tax%)
  const calculateNetAmount = (amount, discountPercentage, selectedTaxes) => {
    if (!amount || amount <= 0) return 0;
    
    const discountAmount = amount * (discountPercentage || 0) / 100;
    const amountAfterDiscount = amount - discountAmount;
    
    const taxPercentage = selectedTaxes.reduce((total, tax) => {
      const taxData = taxes.find(t => t.tax_Id === tax.value);
      return total + (taxData ? taxData.tax_Perc : 0);
    }, 0);
    const taxAmount = amountAfterDiscount * taxPercentage / 100;
    
    return amountAfterDiscount + taxAmount;
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Packages Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Package</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Packages List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      {isUsedInBilling && (
                        <div className="alert alert-warning" role="alert">
                          <i className="bx bx-info-circle me-2"></i>
                          <strong>Limited Editing:</strong> This package has been used in billing. 
                          Only amount, discount, and tax can be modified. Package Code, Package Name, and Services cannot be changed.
                        </div>
                      )}
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PackageCode">Package Code <span className="text-danger">*</span></Label>
                              <Input
                                id="PackageCode"
                                name="PackageCode"
                                type="text"
                                value={form.PackageCode}
                                onChange={(e) => setForm({ ...form, PackageCode: e.target.value })}
                                placeholder="Enter Package Code"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PackageName_En">Package Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                id="PackageName_En"
                                name="PackageName_En"
                                type="text"
                                value={form.PackageName_En}
                                onChange={(e) => setForm({ ...form, PackageName_En: e.target.value })}
                                placeholder="Enter Package Name in English"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PackageName_Ar">Package Name (Arabic)</Label>
                              <Input
                                id="PackageName_Ar"
                                name="PackageName_Ar"
                                type="text"
                                value={form.PackageName_Ar}
                                onChange={(e) => setForm({ ...form, PackageName_Ar: e.target.value })}
                                placeholder="Enter Package Name in Arabic"
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Amount">Sales Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Amount"
                                name="Amount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.Amount}
                                onChange={(e) => setForm({ ...form, Amount: parseFloat(e.target.value) || 0 })}
                                placeholder="Enter Sales Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="DiscountPercentage">Discount Percentage (%)</Label>
                              <Input
                                id="DiscountPercentage"
                                name="DiscountPercentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={form.DiscountPercentage}
                                onChange={(e) => setForm({ ...form, DiscountPercentage: parseFloat(e.target.value) || 0 })}
                                placeholder="Enter Discount %"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="taxes">Taxes</Label>
                              <Select
                                value={selectedTaxes}
                                onChange={(selected) => setSelectedTaxes(selected || [])}
                                options={taxOptions}
                                placeholder="Select Taxes"
                                styles={selectStyles}
                                isMulti
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="NetAmount">Net Amount</Label>
                              <Input
                                id="NetAmount"
                                name="NetAmount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={calculateNetAmount(form.Amount, form.DiscountPercentage, selectedTaxes).toFixed(2)}
                                placeholder="Calculated Net Amount"
                                readOnly
                                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Service Details Section */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Services <span className="text-danger">*</span></Label>
                            {serviceDetails.map((detail, index) => (
                              <Row key={index} className="mb-3 align-items-end">
                                <Col md={6}>
                                  <FormGroup>
                                    <Label>Service</Label>
                                    <Select
                                      key={`service-${index}-${formKey}`}
                                      value={serviceOptions.find(option => option.value === detail.Service_Id)}
                                      onChange={(selected) => updateServiceDetail(index, 'Service_Id', selected?.value || null)}
                                      options={getAvailableServiceOptions(index)}
                                      placeholder="Select Service"
                                      styles={selectStyles}
                                      isClearable
                                      isSearchable
                                      maxMenuHeight={200}
                                      isDisabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={4}>
                                  <FormGroup>
                                    <Label>Quantity</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={detail.Quantity || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || parseInt(value) >= 1) {
                                          updateServiceDetail(index, 'Quantity', value === '' ? '' : parseInt(value));
                                        }
                                      }}
                                      placeholder="Quantity"
                                      disabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={2}>
                                  <div className="d-flex gap-2">
                                    {serviceDetails.length > 1 && (
                                      <Button
                                        type="button"
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeServiceDetail(index)}
                                        disabled={isUsedInBilling}
                                      >
                                        <Minus size={16} />
                                      </Button>
                                    )}
                                    {index === serviceDetails.length - 1 && (
                                      <Button
                                        type="button"
                                        color="success"
                                        size="sm"
                                        onClick={addServiceDetail}
                                        disabled={isUsedInBilling}
                                      >
                                        <Plus size={16} />
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            ))}
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Cost">Cost Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Cost"
                                name="Cost"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.Cost}
                                onChange={(e) => setForm({ ...form, Cost: parseFloat(e.target.value) || 0 })}
                                placeholder="Enter Cost Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                          {hasDeletePermission && (
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <div className="form-check form-switch mt-4">
                                  <Input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="Status"
                                    checked={form.Status}
                                    onChange={(e) => setForm({ ...form, Status: e.target.checked })}
                                  />
                                  <Label className="form-check-label" for="Status">
                                    {form.Status ? "Active" : "Inactive"}
                                  </Label>
                                </div>
                              </FormGroup>
                            </Col>
                          )}
                        </Row>

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.PackageName_En.trim() || !form.PackageCode.trim()}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Package" : "Save Package"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                              {isUsedInBilling && isEditing && (
                                <Button
                                  type="button"
                                  color="warning"
                                  onClick={() => {
                                    setServiceDetails([...originalServiceDetails]);
                                    Swal.fire("Info", "Service details restored to original selection", "info");
                                  }}
                                  className="waves-effect waves-light"
                                  title="Restore original service selection"
                                >
                                  <i className="bx bx-reset me-1"></i>
                                  Restore Services
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Packages</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterPackageName">Package Name</Label>
                                <Input
                                  id="filterPackageName"
                                  name="filterPackageName"
                                  type="text"
                                  value={filter.PackageName_En}
                                  onChange={(e) => setFilter({ ...filter, PackageName_En: e.target.value })}
                                  placeholder="Search package name..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterPackageCode">Package Code</Label>
                                <Input
                                  id="filterPackageCode"
                                  name="filterPackageCode"
                                  type="text"
                                  value={filter.PackageCode}
                                  onChange={(e) => setFilter({ ...filter, PackageCode: e.target.value })}
                                  placeholder="Search package code..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  value={statusOptions.find(option => option.value === filter.Status)}
                                  onChange={(selected) => setFilter({ ...filter, Status: selected?.value || null })}
                                  options={statusOptions}
                                  placeholder="Select Status"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                  <Button
                                    color="info"
                                    onClick={() => {
                                      setBillingUsageMap({});
                                      loadSpaPackages();
                                    }}
                                    className="waves-effect waves-light"
                                    title="Refresh billing usage data"
                                  >
                                    <i className="bx bx-refresh me-1"></i>
                                    Refresh Billing
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Spa Packages List
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => setActiveTab("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Package
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading spa packages...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="10%">Package Code</th>
                                      <th width="15%">Package Name (En)</th>
                                      <th width="15%">Package Name (Ar)</th>
                                      <th width="8%">Cost</th>
                                      <th width="8%">Amount</th>
                                      <th width="8%">Discount %</th>
                                      <th width="8%">Net Amount</th>
                                      <th width="8%">Status</th>
                                      <th width="6%" title="Shows if package has been used in billing (limited editing)">Billing</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.PackageId || item.packageId}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.PackageCode || item.packageCode}</td>
                                          <td>{item.PackageName_En || item.packageName_En}</td>
                                          <td>{(item.PackageName_Ar || item.packageName_Ar) || 'N/A'}</td>
                                          <td className="text-end">{((item.Cost || item.cost) || 0).toFixed(2)}</td>
                                          <td className="text-end">{((item.Amount || item.amount) || 0).toFixed(2)}</td>
                                          <td className="text-end">{((item.DiscountPercentage || item.discountPercentage) || 0).toFixed(2)}%</td>
                                          <td className="text-end">{((item.NetAmount || item.netAmount) || 0).toFixed(2)}</td>
                                          <td>
                                            <span className={`badge ${(item.Status !== undefined ? item.Status : item.status) ? 'bg-success' : 'bg-danger'}`}>
                                              {(item.Status !== undefined ? item.Status : item.status) ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>
                                            {(() => {
                                              const usage = billingUsageMap[item.PackageId || item.packageId];
                                              console.log(`Package ${item.PackageId || item.packageId} billing usage:`, usage);
                                              if (usage === true) {
                                                return <span className="badge bg-warning text-dark">Used</span>;
                                              } else if (usage === false) {
                                                return <span className="badge bg-success">Available</span>;
                                              } else {
                                                return <span className="badge bg-secondary">Unknown</span>;
                                              }
                                            })()}
                                          </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                                title="View"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleExportDetails(item.PackageId || item.packageId)}
                                                disabled={loading}
                                                className="waves-effect waves-light"
                                                title="Export Details"
                                              >
                                                <Download size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === (item.PackageId || item.packageId)}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === (item.PackageId || item.packageId) ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="11" className="text-center">
                                          {hasAppliedFilter ? "No spa packages found matching the filter criteria." : "No spa packages available. Please add some packages first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {startIndex + 1} to {Math.min(endIndex, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {totalPages}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === totalPages}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
